import React, { useState, useEffect, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.turingscout.com';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Account {
  account_id: string;
  name: string;
  contact_email: string;
  status: 'active' | 'suspended';
  recruiter_count: number;
  created_at: string;
  updated_at: string;
  users?: User[];
}

interface User {
  user_id: string;
  account_id: string;
  email: string;
  name: string;
  role: 'recruiter' | 'admin' | 'superadmin';
  status: string;
  created_at: string;
}

// ── Design system tokens ──────────────────────────────────────────────────────

const CSS = `
  :root {
    --accent: oklch(52% 0.22 265);
    --accent-light: oklch(95% 0.06 265);
    --accent-hover: oklch(46% 0.22 265);
    --bg: oklch(98.5% 0.005 260);
    --bg-2: oklch(96% 0.008 260);
    --bg-3: oklch(93% 0.010 260);
    --border: oklch(88% 0.012 260);
    --text-1: oklch(18% 0.015 260);
    --text-2: oklch(46% 0.018 260);
    --text-3: oklch(62% 0.015 260);
    --green: oklch(50% 0.17 155);
    --green-light: oklch(95% 0.05 155);
    --red: oklch(52% 0.20 25);
    --red-light: oklch(95% 0.05 25);
    --amber: oklch(58% 0.18 75);
    --amber-light: oklch(96% 0.05 75);
    --shadow-sm: 0 1px 3px oklch(0% 0 0 / 0.08), 0 1px 2px oklch(0% 0 0 / 0.06);
    --shadow-md: 0 4px 12px oklch(0% 0 0 / 0.10), 0 2px 4px oklch(0% 0 0 / 0.06);
  }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Inter', system-ui, sans-serif; background: var(--bg); color: var(--text-1); }
  input, button { font-family: inherit; }
`;

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Icon ──────────────────────────────────────────────────────────────────────

const icons: Record<string, React.ReactNode> = {
  lock: <><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>,
  building: <><path d="M3 21h18"/><path d="M9 21V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v16"/><path d="M3 7h3"/><path d="M3 11h3"/><path d="M3 15h3"/><path d="M15 7h3"/><path d="M15 11h3"/><path d="M15 15h3"/></>,
  plus: <><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></>,
  check: <><polyline points="20 6 9 12 4 9"/></>,
  x: <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>,
  edit: <><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></>,
  refresh: <><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></>,
  shield: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>,
  eye: <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>,
  eyeOff: <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></>,
  logout: <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></>,
  trash: <><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></>,
  ban: <><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></>,
  userCheck: <><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><polyline points="17 11 19 13 23 9"/></>,
};

const Icon: React.FC<{ name: string; size?: number; color?: string }> = ({ name, size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {icons[name]}
  </svg>
);

// ── Modal ─────────────────────────────────────────────────────────────────────

interface ModalProps { title: string; onClose: () => void; children: React.ReactNode; }
const Modal: React.FC<ModalProps> = ({ title, onClose, children }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'oklch(0% 0 0 / 0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }}>
    <div style={{ background: '#fff', borderRadius: 16, boxShadow: 'var(--shadow-md)', width: '100%', maxWidth: 480, padding: 28 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h3 style={{ fontSize: 18, fontWeight: 600 }}>{title}</h3>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', padding: 4, borderRadius: 6, display: 'flex' }}>
          <Icon name="x" size={18} />
        </button>
      </div>
      {children}
    </div>
  </div>
);

// ── Input ─────────────────────────────────────────────────────────────────────

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> { label: string; }
const Input: React.FC<InputProps> = ({ label, ...props }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-2)' }}>{label}</label>
    <input
      {...props}
      style={{ padding: '10px 14px', borderRadius: 8, border: '1.5px solid var(--border)', background: 'var(--bg-2)', fontSize: 14, color: 'var(--text-1)', outline: 'none', transition: 'border-color 0.15s', ...props.style }}
      onFocus={e => (e.target.style.borderColor = 'var(--accent)')}
      onBlur={e => (e.target.style.borderColor = 'var(--border)')}
    />
  </div>
);

// ── RootAdmin page ────────────────────────────────────────────────────────────

const RootAdmin: React.FC = () => {
  const [apiKey, setApiKey] = useState('');
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState('');
  const [loading, setLoading] = useState(false);

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [fetchError, setFetchError] = useState('');

  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createEmail, setCreateEmail] = useState('');
  const [createLoading, setCreateLoading] = useState(false);

  const [editAccount, setEditAccount] = useState<Account | null>(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editStatus, setEditStatus] = useState<'active' | 'suspended'>('active');
  const [editLoading, setEditLoading] = useState(false);

  const [expandedAccount, setExpandedAccount] = useState<string | null>(null);
  const [addUserAccount, setAddUserAccount] = useState<Account | null>(null);
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState<User['role']>('recruiter');
  const [addUserLoading, setAddUserLoading] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState<{ type: 'user' | 'account'; id: string; name: string; accountId?: string } | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Persist key in sessionStorage across soft navigations
  useEffect(() => {
    const saved = sessionStorage.getItem('ts_admin_key');
    if (saved) { setApiKey(saved); setAuthed(true); }
  }, []);

  const fetchAccounts = useCallback(async (key: string) => {
    setLoading(true);
    setFetchError('');
    try {
      const res = await fetch(`${API_BASE}/admin/accounts`, {
        headers: { 'X-Root-Admin-Key': key },
      });
      if (res.status === 403) { setAuthError('Invalid API key'); setAuthed(false); return; }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setAccounts(data.accounts || []);
    } catch (e: any) {
      setFetchError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleAuth = (e: React.FormEvent) => {
    e.preventDefault();
    if (!apiKeyInput.trim()) return;
    setAuthError('');
    sessionStorage.setItem('ts_admin_key', apiKeyInput.trim());
    setApiKey(apiKeyInput.trim());
    setAuthed(true);
    fetchAccounts(apiKeyInput.trim());
  };

  useEffect(() => {
    if (authed && apiKey) fetchAccounts(apiKey);
  }, [authed, apiKey, fetchAccounts]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/accounts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Root-Admin-Key': apiKey },
        body: JSON.stringify({ name: createName.trim(), contact_email: createEmail.trim() }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const account = await res.json();
      setAccounts(prev => [...prev, account]);
      setShowCreate(false);
      setCreateName('');
      setCreateEmail('');
    } catch (e: any) {
      alert(`Create failed: ${e.message}`);
    } finally {
      setCreateLoading(false);
    }
  };

  const openEdit = (acc: Account) => {
    setEditAccount(acc);
    setEditName(acc.name);
    setEditEmail(acc.contact_email);
    setEditStatus(acc.status);
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editAccount) return;
    setEditLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/accounts/${editAccount.account_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-Root-Admin-Key': apiKey },
        body: JSON.stringify({ name: editName.trim(), contact_email: editEmail.trim(), status: editStatus }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updated = await res.json();
      setAccounts(prev => prev.map(a => a.account_id === updated.account_id ? updated : a));
      setEditAccount(null);
    } catch (e: any) {
      alert(`Update failed: ${e.message}`);
    } finally {
      setEditLoading(false);
    }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addUserAccount) return;
    setAddUserLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/accounts/${addUserAccount.account_id}/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Root-Admin-Key': apiKey },
        body: JSON.stringify({ name: newUserName.trim(), email: newUserEmail.trim(), password: newUserPassword, role: newUserRole }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const newUser: User = await res.json();
      setAccounts(prev => prev.map(a =>
        a.account_id === addUserAccount.account_id
          ? { ...a, users: [...(a.users || []), newUser], recruiter_count: (a.recruiter_count || 0) + 1 }
          : a
      ));
      setAddUserAccount(null);
      setNewUserName(''); setNewUserEmail(''); setNewUserPassword(''); setNewUserRole('recruiter');
    } catch (e: any) {
      alert(`Failed: ${e.message}`);
    } finally {
      setAddUserLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('ts_admin_key');
    setApiKey('');
    setApiKeyInput('');
    setAuthed(false);
    setAccounts([]);
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    setDeleteLoading(true);
    try {
      const url = confirmDelete.type === 'user'
        ? `${API_BASE}/admin/users/${confirmDelete.id}`
        : `${API_BASE}/admin/accounts/${confirmDelete.id}`;
      const res = await fetch(url, { method: 'DELETE', headers: { 'X-Root-Admin-Key': apiKey } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      if (confirmDelete.type === 'account') {
        setAccounts(prev => prev.filter(a => a.account_id !== confirmDelete.id));
      } else {
        setAccounts(prev => prev.map(a =>
          a.account_id === confirmDelete.accountId
            ? { ...a, users: (a.users || []).filter(u => u.user_id !== confirmDelete.id) }
            : a
        ));
      }
      setConfirmDelete(null);
    } catch (e: any) {
      alert(`Delete failed: ${e.message}`);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleToggleUserStatus = async (user: User, accountId: string) => {
    const newStatus = user.status === 'active' ? 'suspended' : 'active';
    try {
      const res = await fetch(`${API_BASE}/admin/users/${user.user_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-Root-Admin-Key': apiKey },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updated: User = await res.json();
      setAccounts(prev => prev.map(a =>
        a.account_id === accountId
          ? { ...a, users: (a.users || []).map(u => u.user_id === user.user_id ? updated : u) }
          : a
      ));
    } catch (e: any) {
      alert(`Update failed: ${e.message}`);
    }
  };

  // ── Gate screen ─────────────────────────────────────────────────────────────

  if (!authed) {
    return (
      <>
        <style>{CSS}</style>
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', padding: 16 }}>
          <div style={{ width: '100%', maxWidth: 400 }}>
            <div style={{ textAlign: 'center', marginBottom: 32 }}>
              <div style={{ width: 56, height: 56, borderRadius: 16, background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <Icon name="shield" size={28} color="#fff" />
              </div>
              <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 6 }}>Root Admin</h1>
              <p style={{ color: 'var(--text-2)', fontSize: 14 }}>Enter your admin API key to manage accounts</p>
            </div>

            <form onSubmit={handleAuth} style={{ background: '#fff', borderRadius: 16, padding: 28, boxShadow: 'var(--shadow-md)', display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-2)' }}>API Key</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showKey ? 'text' : 'password'}
                    value={apiKeyInput}
                    onChange={e => setApiKeyInput(e.target.value)}
                    placeholder="Enter root admin key"
                    autoFocus
                    style={{ width: '100%', padding: '10px 44px 10px 14px', borderRadius: 8, border: '1.5px solid var(--border)', background: 'var(--bg-2)', fontSize: 14, color: 'var(--text-1)', outline: 'none', fontFamily: 'monospace' }}
                    onFocus={e => (e.target.style.borderColor = 'var(--accent)')}
                    onBlur={e => (e.target.style.borderColor = 'var(--border)')}
                  />
                  <button type="button" onClick={() => setShowKey(!showKey)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', display: 'flex', padding: 2 }}>
                    <Icon name={showKey ? 'eyeOff' : 'eye'} size={16} />
                  </button>
                </div>
              </div>
              {authError && <p style={{ color: 'var(--red)', fontSize: 13 }}>{authError}</p>}
              <button type="submit" style={{ padding: '11px 0', borderRadius: 8, background: 'var(--accent)', color: '#fff', border: 'none', fontSize: 15, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'background 0.15s' }} onMouseEnter={e => ((e.target as HTMLElement).style.background = 'var(--accent-hover)')} onMouseLeave={e => ((e.target as HTMLElement).style.background = 'var(--accent)')}>
                <Icon name="lock" size={16} color="#fff" />
                Unlock Admin
              </button>
            </form>
          </div>
        </div>
      </>
    );
  }

  // ── Main admin UI ────────────────────────────────────────────────────────────

  const activeCount = accounts.filter(a => a.status === 'active').length;
  const totalRecruiters = accounts.reduce((s, a) => s + (a.recruiter_count || 0), 0);

  return (
    <>
      <style>{CSS}</style>
      <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>

        {/* Header */}
        <header style={{ background: '#fff', borderBottom: '1.5px solid var(--border)', padding: '0 32px', height: 64, display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="shield" size={18} color="#fff" />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, lineHeight: 1 }}>TuringScout</div>
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>Root Admin</div>
            </div>
          </div>

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={() => fetchAccounts(apiKey)} style={{ background: 'none', border: '1.5px solid var(--border)', borderRadius: 8, padding: '7px 14px', fontSize: 13, cursor: 'pointer', color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="refresh" size={14} />
              Refresh
            </button>
            <button onClick={handleLogout} style={{ background: 'none', border: '1.5px solid var(--border)', borderRadius: 8, padding: '7px 14px', fontSize: 13, cursor: 'pointer', color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="logout" size={14} />
              Sign out
            </button>
          </div>
        </header>

        <main style={{ maxWidth: 1100, margin: '0 auto', padding: '40px 32px' }}>

          {/* Page title + create button */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 }}>
            <div>
              <h2 style={{ fontSize: 26, fontWeight: 700, marginBottom: 4 }}>Company Accounts</h2>
              <p style={{ color: 'var(--text-2)', fontSize: 14 }}>Each account represents a company with its own recruiter team.</p>
            </div>
            <button onClick={() => setShowCreate(true)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', transition: 'background 0.15s', flexShrink: 0 }} onMouseEnter={e => ((e.target as HTMLElement).style.background = 'var(--accent-hover)')} onMouseLeave={e => ((e.target as HTMLElement).style.background = 'var(--accent)')}>
              <Icon name="plus" size={16} color="#fff" />
              New Account
            </button>
          </div>

          {/* Stats strip */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 32 }}>
            {[
              { label: 'Total Accounts', value: accounts.length, icon: 'building' },
              { label: 'Active Accounts', value: activeCount, icon: 'check' },
              { label: 'Total Recruiters', value: totalRecruiters, icon: 'refresh' },
            ].map(stat => (
              <div key={stat.label} style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', boxShadow: 'var(--shadow-sm)', display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ width: 44, height: 44, borderRadius: 10, background: 'var(--accent-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon name={stat.icon} size={20} color="var(--accent)" />
                </div>
                <div>
                  <div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1 }}>{stat.value}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 2 }}>{stat.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Error / loading */}
          {fetchError && (
            <div style={{ padding: '14px 18px', background: 'var(--red-light)', color: 'var(--red)', borderRadius: 10, marginBottom: 20, fontSize: 14 }}>
              Failed to load accounts: {fetchError}
            </div>
          )}

          {/* Accounts table */}
          <div style={{ background: '#fff', borderRadius: 14, boxShadow: 'var(--shadow-sm)', overflow: 'hidden' }}>
            <div style={{ padding: '18px 24px', borderBottom: '1.5px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 600, fontSize: 15 }}>All Accounts</span>
              {loading && <span style={{ fontSize: 13, color: 'var(--text-3)' }}>Loading…</span>}
            </div>

            {accounts.length === 0 && !loading ? (
              <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-3)' }}>
                <Icon name="building" size={40} color="var(--border)" />
                <p style={{ marginTop: 12, fontSize: 15 }}>No accounts yet</p>
                <p style={{ fontSize: 13, marginTop: 4 }}>Create the first company account to get started.</p>
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1.5px solid var(--border)' }}>
                    {['Company', 'Contact', 'Users', 'Status', 'Created', ''].map(h => (
                      <th key={h} style={{ padding: '12px 20px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((acc, idx) => (
                    <React.Fragment key={acc.account_id}>
                      <tr style={{ borderBottom: expandedAccount === acc.account_id ? 'none' : idx < accounts.length - 1 ? '1px solid var(--border)' : 'none', transition: 'background 0.1s' }}>
                        <td style={{ padding: '14px 20px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 34, height: 34, borderRadius: 8, background: 'var(--accent-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: 'var(--accent)', flexShrink: 0 }}>
                              {acc.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 14 }}>{acc.name}</div>
                              <div style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'monospace' }}>{acc.account_id.slice(0, 8)}…</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '14px 20px', fontSize: 14, color: 'var(--text-2)' }}>{acc.contact_email}</td>
                        <td style={{ padding: '14px 20px' }}>
                          <button onClick={() => setExpandedAccount(expandedAccount === acc.account_id ? null : acc.account_id)}
                            style={{ background: 'none', border: '1.5px solid var(--border)', borderRadius: 7, padding: '4px 10px', fontSize: 13, cursor: 'pointer', color: 'var(--text-2)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                            {(acc.users?.length ?? 0)} user{(acc.users?.length ?? 0) !== 1 ? 's' : ''} {expandedAccount === acc.account_id ? '▲' : '▼'}
                          </button>
                        </td>
                        <td style={{ padding: '14px 20px' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600, background: acc.status === 'active' ? 'var(--green-light)' : 'var(--red-light)', color: acc.status === 'active' ? 'var(--green)' : 'var(--red)' }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor', display: 'inline-block' }} />
                            {acc.status === 'active' ? 'Active' : 'Suspended'}
                          </span>
                        </td>
                        <td style={{ padding: '14px 20px', fontSize: 13, color: 'var(--text-3)' }}>{fmtDate(acc.created_at)}</td>
                        <td style={{ padding: '14px 20px' }}>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => openEdit(acc)} style={{ background: 'none', border: '1.5px solid var(--border)', borderRadius: 7, padding: '5px 12px', fontSize: 13, cursor: 'pointer', color: 'var(--text-2)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <Icon name="edit" size={13} />
                              Edit
                            </button>
                            <button onClick={() => setConfirmDelete({ type: 'account', id: acc.account_id, name: acc.name })} style={{ background: 'none', border: '1.5px solid var(--red)', borderRadius: 7, padding: '5px 12px', fontSize: 13, cursor: 'pointer', color: 'var(--red)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <Icon name="trash" size={13} />
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                      {expandedAccount === acc.account_id && (
                        <tr style={{ borderBottom: idx < accounts.length - 1 ? '1px solid var(--border)' : 'none' }}>
                          <td colSpan={6} style={{ padding: '0 20px 16px 68px', background: 'var(--bg)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, paddingTop: 12 }}>
                              <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-2)' }}>Users</span>
                              <button onClick={() => setAddUserAccount(acc)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 12px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
                                <Icon name="plus" size={12} color="#fff" /> Add User
                              </button>
                            </div>
                            {!acc.users || acc.users.length === 0 ? (
                              <p style={{ fontSize: 13, color: 'var(--text-3)', fontStyle: 'italic' }}>No users yet. Add the first user to enable portal access.</p>
                            ) : (
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr>
                                    {['Name', 'Email', 'Role', 'Status', ''].map(h => (
                                      <th key={h} style={{ padding: '6px 12px 6px 0', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {acc.users.map(u => (
                                    <tr key={u.user_id}>
                                      <td style={{ padding: '7px 12px 7px 0', fontSize: 13.5, fontWeight: 600 }}>{u.name}</td>
                                      <td style={{ padding: '7px 12px 7px 0', fontSize: 13, color: 'var(--text-2)' }}>{u.email}</td>
                                      <td style={{ padding: '7px 12px 7px 0' }}>
                                        <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 11.5, fontWeight: 600, background: u.role === 'superadmin' ? 'oklch(94% 0.05 195)' : u.role === 'admin' ? 'oklch(94% 0.05 295)' : 'var(--accent-light)', color: u.role === 'superadmin' ? 'oklch(40% 0.15 195)' : u.role === 'admin' ? 'oklch(40% 0.15 295)' : 'var(--accent)' }}>
                                          {u.role === 'superadmin' ? 'Super Admin' : u.role === 'admin' ? 'Admin' : 'Recruiter'}
                                        </span>
                                      </td>
                                      <td style={{ padding: '7px 12px 7px 0' }}>
                                        <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 11.5, fontWeight: 600, background: u.status === 'active' ? 'var(--green-light)' : 'var(--red-light)', color: u.status === 'active' ? 'var(--green)' : 'var(--red)' }}>
                                          {u.status === 'active' ? 'Active' : 'Suspended'}
                                        </span>
                                      </td>
                                      <td style={{ padding: '7px 0' }}>
                                        <div style={{ display: 'flex', gap: 6 }}>
                                          <button
                                            onClick={() => handleToggleUserStatus(u, acc.account_id)}
                                            title={u.status === 'active' ? 'Deactivate user' : 'Reactivate user'}
                                            style={{ background: 'none', border: `1.5px solid ${u.status === 'active' ? 'var(--amber)' : 'var(--green)'}`, borderRadius: 6, padding: '3px 8px', fontSize: 11.5, cursor: 'pointer', color: u.status === 'active' ? 'var(--amber)' : 'var(--green)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                            <Icon name={u.status === 'active' ? 'ban' : 'userCheck'} size={11} />
                                            {u.status === 'active' ? 'Deactivate' : 'Reactivate'}
                                          </button>
                                          <button
                                            onClick={() => setConfirmDelete({ type: 'user', id: u.user_id, name: u.name, accountId: acc.account_id })}
                                            title="Delete user"
                                            style={{ background: 'none', border: '1.5px solid var(--red)', borderRadius: 6, padding: '3px 8px', fontSize: 11.5, cursor: 'pointer', color: 'var(--red)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                            <Icon name="trash" size={11} />
                                            Delete
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </main>
      </div>

      {/* Create modal */}
      {showCreate && (
        <Modal title="New Company Account" onClose={() => setShowCreate(false)}>
          <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Input label="Company Name" value={createName} onChange={e => setCreateName(e.target.value)} placeholder="Acme Corp" required />
            <Input label="Contact Email" type="email" value={createEmail} onChange={e => setCreateEmail(e.target.value)} placeholder="admin@acmecorp.com" required />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
              <button type="button" onClick={() => setShowCreate(false)} style={{ padding: '9px 20px', borderRadius: 8, border: '1.5px solid var(--border)', background: 'none', fontSize: 14, cursor: 'pointer', color: 'var(--text-2)' }}>
                Cancel
              </button>
              <button type="submit" disabled={createLoading} style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: createLoading ? 'not-allowed' : 'pointer', opacity: createLoading ? 0.7 : 1 }}>
                {createLoading ? 'Creating…' : 'Create Account'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Edit modal */}
      {editAccount && (
        <Modal title="Edit Account" onClose={() => setEditAccount(null)}>
          <form onSubmit={handleEdit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Input label="Company Name" value={editName} onChange={e => setEditName(e.target.value)} required />
            <Input label="Contact Email" type="email" value={editEmail} onChange={e => setEditEmail(e.target.value)} required />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-2)' }}>Status</label>
              <select value={editStatus} onChange={e => setEditStatus(e.target.value as 'active' | 'suspended')} style={{ padding: '10px 14px', borderRadius: 8, border: '1.5px solid var(--border)', background: 'var(--bg-2)', fontSize: 14, color: 'var(--text-1)', outline: 'none' }}>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
              <button type="button" onClick={() => setEditAccount(null)} style={{ padding: '9px 20px', borderRadius: 8, border: '1.5px solid var(--border)', background: 'none', fontSize: 14, cursor: 'pointer', color: 'var(--text-2)' }}>
                Cancel
              </button>
              <button type="submit" disabled={editLoading} style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: editLoading ? 'not-allowed' : 'pointer', opacity: editLoading ? 0.7 : 1 }}>
                {editLoading ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Confirm Delete modal */}
      {confirmDelete && (
        <Modal title={`Delete ${confirmDelete.type === 'account' ? 'Account' : 'User'}`} onClose={() => setConfirmDelete(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <p style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.6 }}>
              Are you sure you want to permanently delete <strong>{confirmDelete.name}</strong>?
              {confirmDelete.type === 'account' && ' All users belonging to this account will still exist in the database but lose portal access.'}
              {' '}This action cannot be undone.
            </p>
            <div style={{ padding: '12px 16px', background: 'var(--red-light)', borderRadius: 8, fontSize: 13, color: 'var(--red)' }}>
              This will permanently remove the {confirmDelete.type} from the system.
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setConfirmDelete(null)} style={{ padding: '9px 20px', borderRadius: 8, border: '1.5px solid var(--border)', background: 'none', fontSize: 14, cursor: 'pointer', color: 'var(--text-2)' }}>
                Cancel
              </button>
              <button type="button" onClick={handleConfirmDelete} disabled={deleteLoading} style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: 'var(--red)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: deleteLoading ? 'not-allowed' : 'pointer', opacity: deleteLoading ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Icon name="trash" size={14} color="#fff" />
                {deleteLoading ? 'Deleting…' : `Delete ${confirmDelete.type === 'account' ? 'Account' : 'User'}`}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Add User modal */}
      {addUserAccount && (
        <Modal title={`Add User — ${addUserAccount.name}`} onClose={() => setAddUserAccount(null)}>
          <form onSubmit={handleAddUser} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Input label="Full Name" value={newUserName} onChange={e => setNewUserName(e.target.value)} placeholder="Jane Smith" required />
            <Input label="Email" type="email" value={newUserEmail} onChange={e => setNewUserEmail(e.target.value)} placeholder="jane@company.com" required />
            <Input label="Password" type="password" value={newUserPassword} onChange={e => setNewUserPassword(e.target.value)} placeholder="Minimum 8 characters" required minLength={8} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-2)' }}>Role</label>
              <select value={newUserRole} onChange={e => setNewUserRole(e.target.value as User['role'])} style={{ padding: '10px 14px', borderRadius: 8, border: '1.5px solid var(--border)', background: 'var(--bg-2)', fontSize: 14, color: 'var(--text-1)', outline: 'none' }}>
                <option value="recruiter">Recruiter</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
              <button type="button" onClick={() => setAddUserAccount(null)} style={{ padding: '9px 20px', borderRadius: 8, border: '1.5px solid var(--border)', background: 'none', fontSize: 14, cursor: 'pointer', color: 'var(--text-2)' }}>
                Cancel
              </button>
              <button type="submit" disabled={addUserLoading} style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: addUserLoading ? 'not-allowed' : 'pointer', opacity: addUserLoading ? 0.7 : 1 }}>
                {addUserLoading ? 'Creating…' : 'Create User'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
};

export default RootAdmin;
